import React, { useEffect, useMemo, useState } from 'react';
import {
	InsuranceReport,
	InsuranceReportBatch,
} from '../../../../graphql/graphql';
import styles from './InsuranceExportedBatchesItemsList.module.scss';
import ManagerInsuranceReportListItem from '../ManagerInsuranceReportList/ManagerInsuranceReportListItem';
import Checkbox from '../../../../Components/Base/Checkbox/Checkbox';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import InsuranceReportBulkUpdateModal from '../InsuranceReportBulkUpdate/InsuranceReportBulkUpdateModal';

interface Props {
	items: InsuranceReportBatch[];
	onReload: () => void;
}

const InsuranceExportedBatchesItemsList: React.FC<Props> = ({
	items,
	onReload,
}) => {
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false);

	const reportIds = useMemo(
		() =>
			items
				.map(item => item.insuranceReport?.id)
				.filter((id): id is string => !!id),
		[items],
	);

	useEffect(() => {
		setSelectedIds(prev => prev.filter(id => reportIds.includes(id)));
	}, [reportIds]);

	const isAllSelected =
		reportIds.length > 0 && selectedIds.length === reportIds.length;

	const handleSelectAll = (value: boolean) => {
		setSelectedIds(value ? reportIds : []);
	};

	const handleSelect = (id: string, value: boolean) => {
		setSelectedIds(prev =>
			value ? [...prev, id] : prev.filter(selectedId => selectedId !== id),
		);
	};

	const handleBulkUpdate = () => {
		setIsBulkUpdateOpen(false);
		setSelectedIds([]);
		onReload();
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.controls}>
				<Checkbox
					tx='component.insuranceExportedBatchesItemsList.selectAll'
					value={isAllSelected}
					onChange={handleSelectAll}
				/>
				<LinkButton
					title='component.insuranceExportedBatchesItemsList.bulkUpdate'
					theme={Theme.BLUE}
					disabled={selectedIds.length === 0}
					onClick={() => setIsBulkUpdateOpen(true)}
				/>
			</div>
			{items.map(item => {
				const report = item.insuranceReport as InsuranceReport;
				return (
					<ManagerInsuranceReportListItem
						key={item.id}
						item={report}
						highlightByState
						isSelected={selectedIds.includes(report.id)}
						onSelect={value => handleSelect(report.id, value)}
						onReload={onReload}
						onDelete={onReload}
					/>
				);
			})}
			{isBulkUpdateOpen && (
				<InsuranceReportBulkUpdateModal
					isOpen
					ids={selectedIds}
					onUpdate={handleBulkUpdate}
					onClose={() => setIsBulkUpdateOpen(false)}
				/>
			)}
		</div>
	);
};

export default InsuranceExportedBatchesItemsList;
